import { Loading, Message } from 'element-ui'
import store from '@store'
import { convertQuestions } from '@/views/question-editor/format/convert'
import AnswerFileFullScreen from '../AnswerFileFullScreen'

const DEFAULT_QUESTION = {
  title: '录音查看',
  text: '',
  images: [],
}

function parseJson (value, fallback) {
  if (value == null || value === '') {
    return fallback
  }
  if (typeof value === 'object') {
    return value
  }
  try {
    return JSON.parse(value)
  } catch (e) {
    return fallback
  }
}

function uniqueUrls (urls) {
  const seen = {}
  const list = []
  ;(urls || []).forEach((url) => {
    if (!url || seen[url]) return
    seen[url] = true
    list.push(url)
  })
  return list
}

/**
 * 从工单详情 / ASR 结果中收集录音文件列表
 */
export function collectOrderRecordFiles (orderDetail, asrCheckResult) {
  const fromAsr = uniqueUrls(
    asrCheckResult && Array.isArray(asrCheckResult.record_urls)
      ? asrCheckResult.record_urls
      : []
  )
  if (fromAsr.length) {
    return fromAsr.map(src => ({ src, type: 'record' }))
  }

  const templatejson = parseJson(orderDetail && orderDetail.templatejson, {})
  const answers = parseJson(orderDetail && orderDetail.templateresultjson, [])
  if (templatejson.through_record === '1' && answers[0] && Array.isArray(answers[0].records)) {
    return uniqueUrls(answers[0].records).map(src => ({ src, type: 'record' }))
  }

  const segmentUrls = []
  const items = (asrCheckResult && asrCheckResult.items) || []
  items.forEach((item) => {
    const segments = Array.isArray(item.asr_segments) ? item.asr_segments : []
    segments.forEach((seg) => {
      if (seg && seg.record_url) {
        segmentUrls.push(seg.record_url)
      }
    })
  })
  return uniqueUrls(segmentUrls).map(src => ({ src, type: 'record' }))
}

/**
 * 解析右侧栏题目说明（与审单页全程录音一致：优先第 1 题）
 */
export function resolveRecordQuestionContext (orderDetail, asrCheckResult) {
  const templatejson = parseJson(orderDetail && orderDetail.templatejson, {})
  const steps = Array.isArray(templatejson.steps) ? templatejson.steps : []
  let questions = []
  try {
    questions = steps.length ? convertQuestions(steps) : []
  } catch (e) {
    console.warn('resolveRecordQuestionContext convertQuestions failed', e)
    questions = []
  }

  if (!questions.length) {
    return {
      question: Object.assign({}, DEFAULT_QUESTION, {
        title: `工单 ${(orderDetail && orderDetail.orderid) || ''} 录音`.trim(),
      }),
      questionIndex: 0,
    }
  }

  // 全程录音：审单页挂在第 1 题上（录音风险检查也只检第一题全程录音）
  if (String(templatejson.through_record) === '1') {
    return { question: questions[0], questionIndex: 0 }
  }

  // 注意：ASR item.question_id 是风险配置问题 ID，不是分步模板题 ID，禁止互匹
  // 非全程录音：优先录音证据题，否则第一题
  const recordIdx = questions.findIndex(q => q.evidenceType === 'record')
  if (recordIdx >= 0) {
    return { question: questions[recordIdx], questionIndex: recordIdx }
  }

  return { question: questions[0], questionIndex: 0 }
}

function resolveOrderDetail (options = {}) {
  if (options.orderDetail && options.orderDetail.orderid) {
    return options.orderDetail
  }
  const fromStore = store.state.orderReview && store.state.orderReview.orderDetail
  if (fromStore && fromStore.orderid) {
    return fromStore
  }
  return options.orderDetail || {}
}

function applySeek (seek) {
  if (!seek) return
  store.commit('orderReview/SET_ASR_CHECK_SEEK', {
    recordUrl: seek.recordUrl || '',
    beginMs: Number(seek.beginMs) || 0,
    alias: seek.alias || '',
  })
}

function fetchAsrCheckResult (orderid) {
  // 无 ASR 结果时静默失败，不打断录音查看
  return request('getAudioAsrCheckOrderResult', { id: orderid })
    .then(({ data }) => data || null)
    .catch(() => null)
}

function cloneJson (value) {
  if (value == null) return value
  try {
    return JSON.parse(JSON.stringify(value))
  } catch (e) {
    return value
  }
}

/**
 * 列表等临时入口写入 orderReview store 前快照，关闭弹窗后还原，避免污染审单页状态
 */
function snapshotOrderReviewStore () {
  const orderReview = store.state.orderReview || {}
  return {
    orderDetail: cloneJson(orderReview.orderDetail || {}),
    asrCheckResult: cloneJson(orderReview.asrCheckResult),
    asrCheckSeek: cloneJson(orderReview.asrCheckSeek),
  }
}

function restoreOrderReviewStore (snapshot) {
  if (!snapshot) return
  // SET_ORDER_DETAIL_DATA 会把 locked 按 '1' 字符串重算，还原时先转回约定格式
  const detail = snapshot.orderDetail && typeof snapshot.orderDetail === 'object'
    ? Object.assign({}, snapshot.orderDetail)
    : {}
  if (Object.keys(detail).length) {
    detail.locked = (detail.locked === true || detail.locked === '1') ? '1' : '0'
    store.commit('orderReview/SET_ORDER_DETAIL_DATA', detail)
  } else {
    store.commit('orderReview/SET_ORDER_DETAIL_DATA', {})
  }
  store.commit('orderReview/SET_ASR_CHECK_RESULT', snapshot.asrCheckResult || null)
  store.commit('orderReview/SET_ASR_CHECK_SEEK', snapshot.asrCheckSeek || null)
}

/**
 * 打开录音全屏查看（与审单页共用 AnswerFileFullScreen + ViewAudio）
 *
 * @param {Object} options
 * @param {number|string} [options.orderid] 仅传 orderid 时自动拉工单与 ASR 结果（风险列表用）
 * @param {Array} [options.files] 已有录音文件时直接打开（审单页用）
 * @param {number} [options.viewIndex=0]
 * @param {Object} [options.question]
 * @param {number} [options.questionIndex=0]
 * @param {Object} [options.orderDetail]
 * @param {Object} [options.toolbar]
 * @param {Function} [options.getLableData]
 * @param {Object} [options.seek] 可选跳转 { recordUrl, beginMs, alias }
 * @param {boolean} [options.loadAsr=true] 按 orderid 打开时是否拉取 ASR 结果
 */
export function openOrderRecordAudioViewer (options = {}) {
  const orderid = options.orderid
  const hasFiles = Array.isArray(options.files) && options.files.length > 0

  if (hasFiles) {
    const orderDetail = resolveOrderDetail(options)
    if (options.loadAsr !== false && orderDetail.orderid && !(store.state.orderReview && store.state.orderReview.asrCheckResult)) {
      fetchAsrCheckResult(orderDetail.orderid).then((data) => {
        store.commit('orderReview/SET_ASR_CHECK_RESULT', data)
      })
    }
    applySeek(options.seek)
    AnswerFileFullScreen({
      type: 'record',
      files: options.files,
      viewIndex: options.viewIndex || 0,
      question: options.question || DEFAULT_QUESTION,
      questionIndex: options.questionIndex || 0,
      orderDetail,
      toolbar: options.toolbar || { delete: false, label: true },
      getLableData: options.getLableData || Function.prototype,
    })
    return Promise.resolve()
  }

  if (!orderid) {
    Message.warning('缺少工单ID，无法打开录音')
    return Promise.reject(new Error('missing orderid'))
  }

  const loading = Loading.service({
    text: '加载录音中...',
    background: 'rgba(0, 0, 0, 0.35)',
  })

  const asrPromise = options.loadAsr === false
    ? Promise.resolve(store.state.orderReview && store.state.orderReview.asrCheckResult)
    : fetchAsrCheckResult(orderid)

  return Promise.all([
    request.alert('getOrderDetail', { id: orderid }),
    asrPromise,
  ]).then(([orderRes, asrCheckResult]) => {
    const orderDetail = (orderRes && orderRes.data) || {}
    const files = collectOrderRecordFiles(orderDetail, asrCheckResult)

    if (!files.length) {
      Message.warning('该工单暂无录音文件')
      return
    }

    const { question, questionIndex } = options.question
      ? { question: options.question, questionIndex: options.questionIndex || 0 }
      : resolveRecordQuestionContext(orderDetail, asrCheckResult)

    // 临时写入 store 供 ViewAudio 读风险字幕；关闭后还原，避免污染审单页
    const storeSnapshot = snapshotOrderReviewStore()
    store.commit('orderReview/SET_ORDER_DETAIL_DATA', orderDetail)
    store.commit('orderReview/SET_ASR_CHECK_RESULT', asrCheckResult)
    applySeek(options.seek)

    AnswerFileFullScreen({
      type: 'record',
      files,
      viewIndex: options.viewIndex || 0,
      question,
      questionIndex,
      orderDetail,
      toolbar: options.toolbar || { delete: false, label: false },
      getLableData: options.getLableData || Function.prototype,
      onClose: () => restoreOrderReviewStore(storeSnapshot),
    })
  }).catch((err) => {
    console.error(err)
    Message.error('加载录音失败')
    return Promise.reject(err)
  }).finally(() => {
    loading.close()
  })
}

const OrderRecordAudioViewer = openOrderRecordAudioViewer
OrderRecordAudioViewer.open = openOrderRecordAudioViewer
OrderRecordAudioViewer.collectFiles = collectOrderRecordFiles
OrderRecordAudioViewer.resolveQuestion = resolveRecordQuestionContext

export default OrderRecordAudioViewer



// WEBPACK FOOTER //
// ./src/views/question-review/components/OrderRecordAudioViewer/index.js